/**
 * 图片错误处理 Hook
 * 统一管理图片加载错误状态
 */

import { useState, useCallback } from 'react';

export interface ImageErrorState {
  failedIndices: Set<number>;
  failedUrls: Set<string>;
}

/**
 * 图片错误处理 Hook
 * @param initialFailedUrls 初始失败图片 URL 集合
 * @returns 图片错误状态和处理函数
 */
export function useImageError(initialFailedUrls: Set<string> = new Set()) {
  const [failedIndices, setFailedIndices] = useState<Set<number>>(new Set());
  const [failedUrls, setFailedUrls] = useState<Set<string>>(initialFailedUrls);

  /**
   * 处理图片加载错误
   */
  const handleImageError = useCallback((index: number, url?: string) => {
    setFailedIndices(prev => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });

    if (url) {
      setFailedUrls(prev => {
        const next = new Set(prev);
        next.add(url);
        return next;
      });
    }
  }, []);

  /**
   * 检查索引是否已失败
   */
  const isIndexFailed = useCallback((index: number) => {
    return failedIndices.has(index);
  }, [failedIndices]);

  /**
   * 检查 URL 是否已失败
   */
  const isUrlFailed = useCallback((url: string) => {
    return failedUrls.has(url);
  }, [failedUrls]);

  /**
   * 重置错误状态
   */
  const resetErrors = useCallback(() => {
    setFailedIndices(new Set());
    setFailedUrls(new Set());
  }, []);

  return {
    failedIndices,
    failedUrls,
    handleImageError,
    isIndexFailed,
    isUrlFailed,
    resetErrors,
  };
}

/**
 * 生成占位图 URL
 * @param text 占位图文本
 * @returns 占位图 URL
 */
export function generatePlaceholderUrl(text: string = '图片加载失败'): string {
  return `https://via.placeholder.com/300x300/1f2937/6b7280?text=${encodeURIComponent(text)}`;
}

/**
 * 检测图片 URL 是否有效
 * @param url 图片 URL
 * @returns 是否为有效图片路径
 */
export function isValidImageUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return url.startsWith('/assets/') || url.startsWith('http') || url.startsWith('https');
}
